import {
    AlertTriangle,
    ArrowLeft,
    ArrowRight,
    BadgeCheck,
    BadgeX,
    ChevronRight,
    CircleDollarSign,
    LoaderCircle,
    RefreshCw,
    Search,
    Users,
    X,
    Zap,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FaArrowRightLong } from 'react-icons/fa6'
import { toast } from 'react-toastify'
import { useLocation, useNavigate } from 'react-router-dom'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './StoreStaffAssignmentOverviewScreen.module.css'

/* ─────────────────────────── helpers ─────────────────────────── */
const parseActiveStore = (raw) => {
    try {
        return raw && raw.trim() !== '' ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const fmtAmount = (n) =>
    Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

const fmtDate = (d) => {
    if (!d) return ''
    const date = new Date(d)
    return isNaN(date.getTime())
        ? d
        : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const DEFAULT_AVATAR = 'https://cdn.vectorstock.com/i/500p/28/66/gray-profile-silhouette-avatar-vector-21542866.jpg'

/* ─────────────────────────── status config ─────────────────────── */
const STATUS_CONFIG = {
    FULLY_ASSIGNED: {
        label: 'Fully Assigned',
        bg: '#f0fdf4',
        color: '#166534',
        border: '#bbf7d0',
    },
    SALARY_ONLY: {
        label: 'Salary Only',
        bg: '#eff6ff',
        color: '#1e40af',
        border: '#bfdbfe',
    },
    INCENTIVE_ONLY: {
        label: 'No Salary',
        bg: '#fff7ed',
        color: '#9a3412',
        border: '#fed7aa',
    },
    UNASSIGNED: {
        label: 'Unassigned',
        bg: '#fef2f2',
        color: '#991b1b',
        border: '#fecaca',
    },
}

/* ─────────────────────────── filter tabs config ─────────────────── */
const FILTER_TABS = [
    { key: 'ALL', label: 'All', countKey: 'totalStaff' },
    { key: 'PAYROLL_READY', label: 'Payroll Ready', countKey: 'payrollReadyCount' },
    { key: 'SALARY_MISSING', label: 'Salary Missing', countKey: 'salaryMissingCount' },
    { key: 'INCENTIVE_MISSING', label: 'No Commission', countKey: 'incentiveMissingCount' },
    { key: 'FULLY_ASSIGNED', label: 'Fully Assigned', countKey: 'fullyAssignedCount' },
]

const FILTER_KEYS = new Set(FILTER_TABS.map((tab) => tab.key))

/* ─────────────────────────── sub-components ────────────────────── */
const SummaryCard = ({ label, value, color, subLabel }) => (
    <div className={styles.summaryCard} style={{ borderLeftColor: color }}>
        <strong style={{ color }}>{value}</strong>
        <span className={styles.summaryLabel}>{label}</span>
        {subLabel && <span className={styles.summarySubLabel}>{subLabel}</span>}
    </div>
)

const StaffAssignmentCard = ({ staff, onAssignSalary, onAssignPlan, onViewProfile, onReassignSalary, onReviseSalary }) => {
    const cfg = STATUS_CONFIG[staff.assignmentStatus] || STATUS_CONFIG.UNASSIGNED

    return (
        <article className={styles.staffCard}>
            {/* Header */}
            <div className={styles.cardHeader}>
                <img
                    src={staff.profileImg || DEFAULT_AVATAR}
                    alt={staff.staffName}
                    className={styles.avatar}
                    onError={(e) => { e.currentTarget.src = DEFAULT_AVATAR }}
                />
                <div className={styles.staffMeta}>
                    <span className={styles.staffName}>{staff.staffName}</span>
                    <span className={styles.designation}>{staff.designationLabel}</span>
                    {staff.joiningDate && (
                        <span className={styles.joiningDate}>Joined {fmtDate(staff.joiningDate)}</span>
                    )}
                </div>
                <span
                    className={styles.statusBadge}
                    style={{ background: cfg.bg, color: cfg.color, borderColor: cfg.border }}
                >
                    {cfg.label}
                </span>
            </div>

            {/* Salary row */}
            {staff.salaryAssignment ? (
                <div className={styles.assignmentRow}>
                    <CircleDollarSign size={14} className={styles.assignIcon} style={{ color: '#2563eb' }} />
                    <div className={styles.assignDetail}>
                        <span className={styles.templateName}>{staff.salaryAssignment.templateName}</span>
                        <span className={styles.templateMeta}>
                            ₹{fmtAmount(staff.salaryAssignment.basicAmount)} basic &middot; from {fmtDate(staff.salaryAssignment.effectiveFrom)}
                        </span>
                        {staff.salaryAssignment.templateIsActive === 0 && (
                            <button
                                type="button"
                                className={styles.archivedWarn}
                                onClick={() => onReassignSalary(staff.staffIdentifier, staff.staffName)}
                            >
                                <AlertTriangle size={12} /> Template archived — reassign to active template
                            </button>
                        )}
                    </div>
                    <BadgeCheck size={16} className={styles.checkIcon} />
                </div>
            ) : (
                <div className={`${styles.assignmentRow} ${styles.missingRow}`}>
                    <BadgeX size={14} className={styles.missingIcon} />
                    <span className={styles.missingText}>No salary structure assigned</span>
                </div>
            )}

            {/* Incentive row */}
            {staff.incentiveAssignment ? (
                <div className={styles.assignmentRow}>
                    <Zap size={14} className={styles.assignIcon} style={{ color: '#7c3aed' }} />
                    <div className={styles.assignDetail}>
                        <span className={styles.templateName}>{staff.incentiveAssignment.templateName}</span>
                        <span className={styles.templateMeta}>
                            from {fmtDate(staff.incentiveAssignment.effectiveFrom)}
                        </span>
                        {staff.incentiveAssignment.templateIsActive === 0 && (
                            <button
                                type="button"
                                className={styles.archivedWarn}
                                onClick={() => onAssignPlan(staff.staffIdentifier, staff.staffName)}
                            >
                                <AlertTriangle size={12} /> Plan archived — reassign to active plan
                            </button>
                        )}
                    </div>
                    <BadgeCheck size={16} className={styles.checkIcon} />
                </div>
            ) : (
                <div className={`${styles.assignmentRow} ${styles.noIncentiveRow}`}>
                    <span className={styles.noIncentiveText}>— No commission plan assigned</span>
                </div>
            )}

            {/* Actions */}
            <div className={styles.actionsRow}>
                {staff.salaryAssignment && (
                    <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.reviseSalaryBtn}`}
                        onClick={() => onReviseSalary(staff)}
                    >
                        Revise Salary <FaArrowRightLong size={11} />
                    </button>
                )}
                {!staff.salaryAssignment && (
                    <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.assignSalaryBtn}`}
                        onClick={() => onAssignSalary(staff.staffIdentifier, staff.staffName)}
                    >
                        Assign Salary <FaArrowRightLong size={11} />
                    </button>
                )}
                {!staff.incentiveAssignment && (
                    <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.assignPlanBtn}`}
                        onClick={() => onAssignPlan(staff.staffIdentifier, staff.staffName)}
                    >
                        Assign Plan <FaArrowRightLong size={11} />
                    </button>
                )}
                {staff.salaryAssignment && staff.incentiveAssignment && (
                    <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.viewProfileBtn}`}
                        onClick={() => onViewProfile(staff.staffIdentifier, staff.staffName)}
                    >
                        View Profile <ChevronRight size={13} />
                    </button>
                )}
            </div>
        </article>
    )
}

/* ─────────────────────────── main screen ─────────────────────── */
const StoreStaffAssignmentOverviewScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [overviewData, setOverviewData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [activeFilter, setActiveFilter] = useState('ALL')
    const [searchText, setSearchText] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const searchRef = useRef(null)

    useEffect(() => {
        const preferred = location?.state?.preferredFilter
        if (preferred && FILTER_KEYS.has(preferred)) {
            setActiveFilter(preferred)
        }
    }, [location?.state?.preferredFilter])

    /* debounce 400ms */
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText), 400)
        return () => clearTimeout(t)
    }, [searchText])

    /* reset page on filter/search change */
    useEffect(() => {
        setCurrentPage(1)
    }, [activeFilter, debouncedSearch])

    /* ── API ── */
    const fetchAssignmentOverview = useCallback(
        async ({ silent = false } = {}) => {
            if (!authCtx?.token || !storeIdentifier) return

            if (silent) {
                setIsRefreshing(true)
            } else {
                setIsLoading(true)
            }

            try {
                const response = await fetch(BASE_URL_API + 'getStoreStaffAssignmentOverview', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier,
                        statusFilter: activeFilter,
                        search: debouncedSearch,
                        page: currentPage,
                        perPage: 25,
                    }),
                })

                if (!response.ok) {
                    toast.error('Failed to load staff assignments.')
                    return
                }

                const respo = await response.json()

                if (respo?.status === 'success') {
                    setOverviewData(respo.response)
                } else {
                    if (respo?.message === 'tokenExpired') {
                        authCtx.logout()
                        return
                    }
                    toast.error(respo?.message || 'Failed to load overview.')
                }
            } catch (error) {
                console.error('fetchAssignmentOverview error:', error)
                toast.error('Network error. Please check your connection.')
            } finally {
                setIsLoading(false)
                setIsRefreshing(false)
            }
        },
        [authCtx, storeIdentifier, activeFilter, debouncedSearch, currentPage]
    )

    useEffect(() => {
        fetchAssignmentOverview({ silent: false })
    }, [fetchAssignmentOverview])

    /* refresh on tab/window focus */
    useEffect(() => {
        const onFocus = () => {
            if (storeIdentifier) fetchAssignmentOverview({ silent: true })
        }
        const onVisibility = () => {
            if (document.visibilityState === 'visible') onFocus()
        }
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [fetchAssignmentOverview, storeIdentifier])

    /* ── navigation ── */
    const handleAssignSalary = (staffId, staffName) => {
        navigate('/payroll/assign-salary-structure', {
            state: { staffIdentifier: staffId, staffName, source: location.pathname },
        })
    }

    const handleAssignPlan = (staffId, staffName) => {
        navigate('/payroll/assign-incentive-plan', {
            state: { staffIdentifier: staffId, staffName, source: location.pathname },
        })
    }

    const handleViewProfile = (staffId, staffName) => {
        navigate(`/payroll/staff-compensation/${staffId}`, {
            state: { staffIdentifier: staffId, staffName, source: location.pathname },
        })
    }

    const handleReviseSalary = (staff) => {
        if (!staff?.salaryAssignment) return
        navigate('/payroll/revise-salary', {
            state: {
                staffIdentifier: staff.staffIdentifier,
                staffName: staff.staffName,
                currentSalaryAssignment: {
                    templateIdentifier: staff.salaryAssignment.templateIdentifier,
                    templateName: staff.salaryAssignment.templateName,
                    basicAmount: staff.salaryAssignment.basicAmount,
                    effectiveFrom: staff.salaryAssignment.effectiveFrom,
                },
            },
        })
    }

    /* ── derived values ── */
    const totalStaff = Number(overviewData?.totalStaff || 0)
    const payrollReadyCount = Number(overviewData?.payrollReadyCount || 0)
    const salaryAssignedCount = Number(overviewData?.salaryAssignedCount || 0)
    const salaryMissingCount = Number(overviewData?.salaryMissingCount || 0)
    const incentiveMissingCount = Number(overviewData?.incentiveMissingCount || 0)
    const fullyAssignedCount = Number(overviewData?.fullyAssignedCount || 0)
    const salaryPct = totalStaff > 0 ? Math.min(100, Math.round((salaryAssignedCount / totalStaff) * 100)) : 0

    const staffList = Array.isArray(overviewData?.staff) ? overviewData.staff : []
    const pagination = overviewData?.pagination || {}

    /* ── no store guard ── */
    if (!storeIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>No store selected.</strong>
                        <p>Please select an active store to view staff salary assignments.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>

            {/* ── Hero ── */}
            <section className={styles.hero}>
                <div className={styles.heroTopRow}>
                    <span className={styles.heroPill}>
                        <Users size={13} /> Staff Salary Setup
                    </span>
                    {isRefreshing && (
                        <span className={styles.refreshHint}>
                            <RefreshCw size={12} className={styles.spinning} /> Syncing…
                        </span>
                    )}
                </div>
                <h1 className={styles.heroTitle}>Staff Assignment Overview</h1>
                <p className={styles.heroSubtitle}>
                    Assign salary structures and commission plans to each staff member before running payroll.
                </p>
            </section>

            {/* ── Summary cards ── */}
            {overviewData && (
                <section className={styles.summaryRow}>
                    <SummaryCard label="Total Staff" value={totalStaff} color="#546E7A" />
                    <SummaryCard
                        label="Payroll Ready"
                        value={payrollReadyCount}
                        color="#2E7D32"
                        subLabel={totalStaff > 0 ? `${Math.round((payrollReadyCount / totalStaff) * 100)}%` : null}
                    />
                    <SummaryCard
                        label="Salary Missing"
                        value={salaryMissingCount}
                        color={salaryMissingCount > 0 ? '#C62828' : '#2E7D32'}
                    />
                    <SummaryCard label="No Commission" value={incentiveMissingCount} color="#1565C0" />
                    <SummaryCard label="Fully Assigned" value={fullyAssignedCount} color="#6A1B9A" />
                </section>
            )}

            {/* ── Progress bar ── */}
            {overviewData && totalStaff > 0 && (
                <section className={styles.progressSection}>
                    <div className={styles.progressLabelRow}>
                        <span className={styles.progressLabel}>
                            Salary assigned to <strong>{salaryAssignedCount}</strong> of <strong>{totalStaff}</strong> staff
                        </span>
                        <span className={styles.progressPct}>{salaryPct}%</span>
                    </div>
                    <div className={styles.progressTrack}>
                        <span
                            className={styles.progressFill}
                            style={{
                                width: `${salaryPct}%`,
                                background: salaryPct >= 100 ? '#22c55e' : salaryPct >= 60 ? '#6366f1' : '#f59e0b',
                            }}
                        />
                    </div>
                </section>
            )}

            {/* ── Search ── */}
            <div className={styles.controlBar}>
                <div className={styles.searchBox}>
                    <Search size={14} className={styles.searchIcon} />
                    <input
                        ref={searchRef}
                        className={styles.searchInput}
                        placeholder="Search by name or designation…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    {searchText && (
                        <button
                            type="button"
                            className={styles.clearSearch}
                            onClick={() => setSearchText('')}
                        >
                            <X size={13} />
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    className={styles.bulkAssignBtn}
                    onClick={() => navigate('/payroll/bulk-assign-salary')}
                >
                    Bulk Assign <FaArrowRightLong size={11} />
                </button>
                <button
                    type="button"
                    className={styles.refreshBtn}
                    onClick={() => fetchAssignmentOverview({ silent: true })}
                    disabled={isRefreshing}
                    title="Refresh"
                >
                    <RefreshCw size={14} className={isRefreshing ? styles.spinning : ''} />
                </button>
            </div>

            {/* ── Filter tabs ── */}
            {overviewData && (
                <div className={styles.filterTabsRow}>
                    {FILTER_TABS.map((tab) => {
                        const count = overviewData[tab.countKey] ?? 0
                        const isActive = activeFilter === tab.key
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                className={`${styles.filterTab} ${isActive ? styles.filterTabActive : ''}`}
                                onClick={() => setActiveFilter(tab.key)}
                            >
                                {tab.label}
                                <span className={`${styles.filterCount} ${isActive ? styles.filterCountActive : ''}`}>
                                    {count}
                                </span>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* ── Staff list ── */}
            <section className={styles.listSection}>
                {isLoading ? (
                    <div className={styles.loaderBox}>
                        <LoaderCircle size={22} className={styles.spinning} />
                        <p>Loading staff assignments…</p>
                    </div>
                ) : staffList.length === 0 ? (
                    <div className={styles.emptyState}>
                        <Users size={36} className={styles.emptyIcon} />
                        <h3>
                            {debouncedSearch
                                ? `No staff matching "${debouncedSearch}"`
                                : activeFilter !== 'ALL'
                                    ? 'No staff matching this filter.'
                                    : 'No active staff found for this store.'}
                        </h3>
                        {(debouncedSearch || activeFilter !== 'ALL') && (
                            <button
                                type="button"
                                className={styles.clearFilterBtn}
                                onClick={() => {
                                    setSearchText('')
                                    setActiveFilter('ALL')
                                }}
                            >
                                Clear filters <X size={12} />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={styles.staffGrid}>
                        {staffList.map((staff) => (
                            <StaffAssignmentCard
                                key={staff.staffIdentifier}
                                staff={staff}
                                onAssignSalary={handleAssignSalary}
                                onAssignPlan={handleAssignPlan}
                                onViewProfile={handleViewProfile}
                                onReassignSalary={handleAssignSalary}
                                onReviseSalary={handleReviseSalary}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* ── Pagination ── */}
            {!isLoading && pagination.totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        type="button"
                        className={`${styles.pageBtn} ${!pagination.hasPrevPage ? styles.pageBtnDisabled : ''}`}
                        disabled={!pagination.hasPrevPage}
                        onClick={() => setCurrentPage((p) => p - 1)}
                    >
                        <ArrowLeft size={14} /> Prev
                    </button>
                    <span className={styles.pageInfo}>
                        Page {pagination.page} of {pagination.totalPages}
                        <span className={styles.pageTotal}> ({pagination.totalItems} staff)</span>
                    </span>
                    <button
                        type="button"
                        className={`${styles.pageBtn} ${!pagination.hasNextPage ? styles.pageBtnDisabled : ''}`}
                        disabled={!pagination.hasNextPage}
                        onClick={() => setCurrentPage((p) => p + 1)}
                    >
                        Next <ArrowRight size={14} />
                    </button>
                </div>
            )}

        </div>
    )
}

export default StoreStaffAssignmentOverviewScreen